'use client'
import Image from 'next/image'
import Link from 'next/link'
import { motion } from 'framer-motion'


export default function HeroSection() {
    return (
        <section className='relative md:max-h-fit   w-full bg-[#141b2c] px-4 sm:px-6'>
            {/* Background gradient circles */}
            <div className='pointer-events-none absolute inset-0 flex items-center justify-center'>
                <motion.div
                    className='h-[280px] w-[280px] rounded-full bg-[#24056d] sm:h-[400px] sm:w-[400px] md:h-[520px] md:w-[520px]'
                    style={{ filter: 'blur(200px)', willChange: 'transform, opacity' }}
                    initial={{ opacity: 0.5, scale: 0.95 }}
                    animate={{ opacity: 0.9, scale: 1.05 }}
                    transition={{ duration: 6, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
                />
                <motion.div
                    className='absolute h-[180px] w-[180px] rounded-full border border-white/50 sm:h-[250px] sm:w-[250px] md:h-[300px] md:w-[300px]'
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 1.2, ease: 'easeOut', delay: 0.1 }}
                />
                <motion.div
                    className='absolute h-[250px] w-[250px] rounded-full border border-white/30 sm:h-[350px] sm:w-[350px] md:h-[450px] md:w-[450px]'
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 1.4, ease: 'easeOut', delay: 0.2 }}
                />
                <motion.div
                    className='absolute h-[360px] w-[360px] rounded-full border border-white/20 sm:h-[500px] sm:w-[500px] md:h-[600px] md:w-[600px]'
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 1.6, ease: 'easeOut', delay: 0.3 }}
                />
            </div>

            {/* Main content */}
            <motion.div
                className='sm:pt-30 relative z-10 mx-auto flex max-w-5xl flex-col items-center gap-2 pt-[189px]  pb-40 text-center md:gap-12'
                initial='hidden'
                animate='show'
                variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.12 } } }}
            >
                {/* Logo + badge */}
                <motion.div className='flex flex-col items-center' variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}>
                    <motion.h2
                        className='h-[90px] whitespace-nowrap bg-gradient-to-r from-[#C184FB] to-pink-400 bg-clip-text text-5xl font-bold leading- text-transparent sm:text-4xl md:text-7xl'
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                    >
                        IntegraAI
                    </motion.h2>

                    <motion.div className='inline-flex rounded-full bg-gradient-to-r from-[#0469ea] to-[#09c2f9] p-[1px]' whileHover={{ scale: 1.03 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }}>
                        <div className='flex items-center justify-center rounded-full bg-[#1d0e50] px-3 py-1'>
                            <span className='bg-gradient-to-r from-[#09c2f9] to-[#0469ea] bg-clip-text text-[10px] font-medium text-transparent sm:text-[10px]'>
                                by Cloudastra Technologies
                            </span>
                        </div>
                    </motion.div>
                </motion.div>

                {/* Title + description */}
                <div className='flex flex-col items-center gap-4 pt-6 sm:pt-8'>
                    <motion.h1
                        className='text-2xl font-inter font-bold lg:!leading-[150%] leading-loose text-white sm:text-3xl md:text-5xl'
                        variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0 } }}
                        transition={{ duration: 0.7, ease: 'easeOut' }}
                    >
                        Simple AI Chat Platform for <br className='hidden md:block' />
                        Enterprise-grade Management
                    </motion.h1>
                    <motion.p
                        className='max-w-4xl font-extralight text-sm leading-relaxed text-white/80 sm:text-base md:text-lg'
                        variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0 } }}
                        transition={{ duration: 0.7, ease: 'easeOut', delay: 0.05 }}
                    >
                        Integra AI is the multi-model chat platform that gives your enterprise the
                        freedom, privacy and control you need to leverage AI safely and
                        efficiently.
                    </motion.p>
                    <motion.p
                        className='text-xs font-normal leading-relaxed text-[#c184fb] sm:text-sm md:text-base'
                        variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0 } }}
                        transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
                    >
                        Start free instantly • No markup on AI costs • Complete data privacy
                    </motion.p>
                </div>

                {/* Buttons */}
                <motion.div className='flex flex-wrap justify-center gap-3 sm:gap-4' variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0 } }}>
                    <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}>
                        <Link
                            href='https://integra-ai.cloudastra.co/'
                            className='rounded-full border font-bold border-blue-700 bg-white px-5 py-2 text-sm text-blue-700 transition hover:bg-blue-700 hover:text-white sm:px-6 sm:text-base'>
                            Start Chatting Now
                        </Link>
                    </motion.div>
                    <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}>
                        <Link
                            href='/contact?ref=integra-ai-home&utm_source=website&utm_medium=cta&utm_campaign=integra_ai&utm_content=integra_ai__home__request_demo'
                            className='rounded-full border border-blue-700 bg-blue-700 px-5 py-2 text-sm text-white transition hover:bg-blue-500 sm:px-6 sm:text-base'>
                            Request Demo
                        </Link>
                    </motion.div>
                </motion.div>
            </motion.div>

           
        </section>
    )
}
